import { isAccessTokenExpired, isSessionRejected, refreshSession, runWithSessionLock } from '@/plugins/axios';
import { isUserLoggedIn } from '@/router/utils';
import authService from '@/services/agency/auth';
import { useAuthStore } from '@/stores/agency/auth';

// Re-check the session when the tab becomes visible again after being hidden at least this long.
const IDLE_RECHECK_AFTER_MS = 5 * 60 * 1000;

// Timers are suspended while the device sleeps, so a heartbeat that fires far later than scheduled
// means the machine just woke up (closing a laptop lid doesn't always fire visibilitychange).
const HEARTBEAT_INTERVAL_MS = 60 * 1000;
const WAKE_DETECTION_GAP_MS = 3 * 60 * 1000;

let hiddenSince: number | null = null;
let inFlightCheck: Promise<void> | null = null;

/**
 * Asks the API whether this browser's remembered session is still alive. Logs out only on a definite
 * "no" — the session expired, was revoked (logout elsewhere, replay detection), or the account lost
 * access. A network or server failure is inconclusive and leaves the session alone.
 */
const checkSession = async (): Promise<void> => {
  // Owner impersonation sessions have no refresh token, so there is nothing to validate.
  if (!isUserLoggedIn() || !localStorage.getItem('refreshToken')) {
    return;
  }

  let isValid: boolean;

  try {
    // Hold the cross-tab session lock so another tab can't rotate the token while it is being checked.
    isValid = await runWithSessionLock(async () => {
      const refreshToken = localStorage.getItem('refreshToken');

      // Logged out in another tab while waiting for the lock — nothing left to check.
      if (!refreshToken) {
        return true;
      }

      const { data } = await authService.validateSession(refreshToken);

      // Without Web Locks, a token rotated by another tab mid-request reads as invalid; that isn't a dead session.
      return data.valid || localStorage.getItem('refreshToken') !== refreshToken;
    });
  } catch {
    return;
  }

  if (!isValid) {
    await useAuthStore().logout();

    return;
  }

  // Refresh up front on launch/resume so the first burst of API calls doesn't all hit 401 together.
  if (isAccessTokenExpired(localStorage.getItem('accessToken'))) {
    try {
      await refreshSession();
    } catch (error) {
      if (isSessionRejected(error)) {
        await useAuthStore().logout();
      }
    }
  }
};

const runSessionCheck = (): Promise<void> => {
  if (!inFlightCheck) {
    inFlightCheck = checkSession().finally(() => {
      inFlightCheck = null;
    });
  }

  return inFlightCheck;
};

/**
 * Validates the remembered session on app launch and whenever the app returns from being idle:
 * tab shown again after IDLE_RECHECK_AFTER_MS, network reconnected, or device woken from sleep.
 */
export const initSessionMonitor = (): void => {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenSince = Date.now();

      return;
    }

    const wasIdle = hiddenSince !== null && Date.now() - hiddenSince >= IDLE_RECHECK_AFTER_MS;

    hiddenSince = null;

    if (wasIdle) {
      runSessionCheck();
    }
  });

  window.addEventListener('online', () => {
    runSessionCheck();
  });

  let lastHeartbeat = Date.now();

  setInterval(() => {
    const now = Date.now();

    if (now - lastHeartbeat >= WAKE_DETECTION_GAP_MS) {
      runSessionCheck();
    }

    lastHeartbeat = now;
  }, HEARTBEAT_INTERVAL_MS);

  // App launch / page reload
  runSessionCheck();
};
